import cloneDeep from 'clone-deep'
import { ISelectOption } from 'stories/components/SelectOptionInputs/types'

import { InputType } from './input.type.constants'
import { loanOptionsConstants } from './loan.constants'
import { NewInputTextOptions, NewInputTypeOptions } from './loan.input.visibility.type'

export type RegisterFormSection = 'company' | 'personal'
export type RegisterFormInputType = keyof typeof NewInputTypeOptions

export interface RegisterFormFieldConfig {
  key: string
  section: RegisterFormSection
  title: string
  inputType: RegisterFormInputType
  visible: boolean
  required: boolean
  custom?: boolean
  type?: keyof typeof NewInputTextOptions | ''
  options?: ISelectOption[]
}

export interface RegisterFormConfig {
  fields: RegisterFormFieldConfig[]
}

export const registerFormSectionTitles: Record<RegisterFormSection, string> = {
  company: 'Company information',
  personal: 'Personal Information',
}

export const registerFormSectionOptions: Record<RegisterFormSection, string> = {
  company: registerFormSectionTitles.company,
  personal: registerFormSectionTitles.personal,
}

export const registerFormInputTypeOptions = NewInputTypeOptions

export const getDefaultAccountTypeOptions = (): ISelectOption[] => [
  { key: 'broker', title: 'Broker', visible: true, isCustom: false },
  { key: 'tablefunder', title: 'Table Funder', visible: true, isCustom: false },
  { key: 'correspondent', title: 'Correspondent', visible: true, isCustom: false },
]

export const getDefaultAccountExecutiveOptionKeys = (): ISelectOption[] => [
  { key: '', title: 'Not working with anyone', visible: true, isCustom: false },
]

export const registerFormEditableSelectFields = ['accountType', 'accountExecutive']

export const registerFormAccountTypes = getDefaultAccountTypeOptions().reduce(
  (acc, option) => {
    acc[option.key] = option.title
    return acc
  },
  {} as Record<string, string>,
)

export const mergeRegisterFormSelectOptions = (
  storedOptions: ISelectOption[] | undefined,
  defaultOptions: ISelectOption[],
): ISelectOption[] => {
  if (!storedOptions?.length) return defaultOptions.map((option) => ({ ...option }))

  const defaultMap = new Map(defaultOptions.map((option) => [option.key, option]))
  const storedKeys = new Set(storedOptions.map((option) => option.key))

  const result = storedOptions
    .filter((option) => defaultMap.has(option.key) || option.isCustom)
    .map((option) => ({
      ...option,
      title: option.title || defaultMap.get(option.key)?.title || option.key,
    }))

  defaultOptions.forEach(({ key, title, isCustom }) => {
    if (!storedKeys.has(key)) result.push({ key, title, visible: true, isCustom })
  })

  return result
}

export const convertRegisterFormSelectOptionsToRecord = (options: ISelectOption[]): Record<string, string> => {
  return options
    .filter((option) => option.visible !== false)
    .reduce((acc, option) => {
      acc[option.key] = option.title
      return acc
    }, {} as Record<string, string>)
}

export const buildAccountExecutiveSelectInput = (
  storedOptions: ISelectOption[] | undefined,
  sharingUsers: Record<string, string>,
) => {
  const dynamicDefaults = Object.entries(sharingUsers).map(([key, title]) => ({
    key,
    title,
    visible: true,
    isCustom: false,
  }))
  const merged = mergeRegisterFormSelectOptions(storedOptions, [
    ...getDefaultAccountExecutiveOptionKeys(),
    ...dynamicDefaults,
  ])
  const emptyOption = merged.find((option) => option.key === '')

  return {
    options: convertRegisterFormSelectOptionsToRecord(merged.filter((option) => option.key !== '')),
    hasDefaultOption: emptyOption?.visible !== false,
    defaultOptionText: emptyOption?.title || 'Not working with anyone',
  }
}

const normalizeRegisterFormInputType = (inputType?: string): RegisterFormInputType => {
  if (inputType && NewInputTypeOptions[inputType as RegisterFormInputType]) return inputType as RegisterFormInputType
  return 'Text'
}

const normalizeSelectOptions = (options?: ISelectOption[] | Record<string, string>): ISelectOption[] | undefined => {
  if (!options) return undefined
  if (Array.isArray(options)) return options
  return Object.entries(options).map(([key, title]) => ({
    key,
    title,
    visible: true,
    isCustom: true,
  }))
}

export const registerFormDefaultGroups: { section: RegisterFormSection; title: string; inputs: Record<string, InputType> }[] =
  [
    {
      section: 'company',
      title: registerFormSectionTitles.company,
      inputs: {
        accountType: {
          inputType: 'select',
          options: registerFormAccountTypes,
          title: 'Registration Type',
          value: 'broker',
          required: true,
        },
        companyName: {
          inputType: 'text',
          title: 'Company Name',
          required: true,
        },
        companyType: {
          inputType: 'select',
          options: loanOptionsConstants.entityType,
          hasDefaultOption: true,
          title: 'Company Type',
          required: true,
        },
        companyAddress: {
          inputType: 'map',
          title: 'Company Address',
          allowForeign: true,
          requireValidate: false,
          required: true,
        },
        companyEIN: {
          inputType: 'text',
          type: 'text',
          title: 'Company EIN',
        },
        companyNmls: {
          inputType: 'text',
          type: 'text',
          title: 'Company NMLS ID',
          required: false,
        },
        accountExecutive: {
          inputType: 'select',
          title: 'Who are you currently working with?',
          required: false,
          options: [],
          hasDefaultOption: true,
          defaultOptionText: 'Not working with anyone',
        },
      },
    },
    {
      section: 'personal',
      title: registerFormSectionTitles.personal,
      inputs: {
        title: {
          inputType: 'text',
          title: 'Principal Title',
        },
        name: {
          inputType: 'text',
          title: 'Full Name',
          required: true,
        },
        email: {
          inputType: 'text',
          type: 'email',
          title: 'Email',
          required: true,
        },
        phone: {
          inputType: 'text',
          type: 'phone',
          title: 'Phone Number',
          required: true,
        },
        phoneExt: {
          inputType: 'text',
          type: 'phone',
          title: 'Phone Ext',
          required: false,
        },
        brokerSSN: {
          inputType: 'text',
          type: 'ssn',
          title: 'Social Security Number',
        },
        brokerNmls: {
          inputType: 'text',
          title: 'NMLS ID',
          required: false,
        },
      },
    },
  ]

export const getDefaultRegisterFormConfig = (): RegisterFormConfig => {
  return {
    fields: registerFormDefaultGroups.flatMap((group) =>
      Object.entries(group.inputs).map(([key, input]) => ({
        key,
        section: group.section,
        title: input.title,
        inputType: input.inputType === 'select' || input.inputType === 'Select' ? 'Select' : 'Text',
        visible: input.visible !== false,
        required: !!input.required,
        custom: false,
        ...(key === 'accountType' && { options: getDefaultAccountTypeOptions() }),
        ...(key === 'accountExecutive' && { options: getDefaultAccountExecutiveOptionKeys() }),
      })),
    ),
  }
}

export const getDefaultRegisterFormGroups = () => cloneDeep(registerFormDefaultGroups)

export const normalizeRegisterFormConfig = (config?: Partial<RegisterFormConfig> | null): RegisterFormConfig => {
  const defaultConfig = getDefaultRegisterFormConfig()
  const defaultFields = defaultConfig.fields.map((field) => {
    const configured = config?.fields?.find((item) => item.key === field.key && !item.custom)
    const normalized: RegisterFormFieldConfig = {
      ...field,
      visible: configured?.visible ?? field.visible,
      required: configured?.required ?? field.required,
    }

    if (field.key === 'accountType') {
      normalized.options = mergeRegisterFormSelectOptions(
        normalizeSelectOptions(configured?.options as any),
        getDefaultAccountTypeOptions(),
      )
    }

    if (field.key === 'accountExecutive') {
      normalized.options = mergeRegisterFormSelectOptions(
        normalizeSelectOptions(configured?.options as any),
        getDefaultAccountExecutiveOptionKeys(),
      )
    }

    return normalized
  })
  const customFields = (config?.fields || [])
    .filter((field) => field.custom)
    .map((field) => ({
      ...field,
      inputType: normalizeRegisterFormInputType(field.inputType as string),
      options: normalizeSelectOptions(field.options as any),
    }))

  return {
    fields: [...defaultFields, ...customFields],
  }
}

export const convertRegisterFormFieldToInput = (field: RegisterFormFieldConfig): InputType => {
  const common = {
    title: field.title,
    required: field.required,
    visible: field.visible,
  }

  switch (field.inputType) {
    case 'Select':
      return {
        ...common,
        inputType: 'select',
        options: (field.options || []) as any,
        hasDefaultOption: true,
      }
    case 'TextArea':
      return {
        ...common,
        inputType: 'textarea',
      }
    case 'ToggleButton':
      return {
        ...common,
        inputType: 'ToggleButton',
        value: false,
      }
    default:
      return {
        ...common,
        inputType: 'text',
        type: (field.type || 'text') as any,
      }
  }
}

export const buildRegisterFormGroups = (config?: Partial<RegisterFormConfig> | null) => {
  const normalizedConfig = normalizeRegisterFormConfig(config)
  const groups = getDefaultRegisterFormGroups()

  groups.forEach((group) => {
    Object.keys(group.inputs).forEach((key) => {
      const field = normalizedConfig.fields.find((item) => item.key === key && !item.custom)
      if (!field) return
      group.inputs[key].visible = field.visible
      group.inputs[key].required = field.required

      if (key === 'accountType' && field.options?.length) {
        group.inputs[key].options = convertRegisterFormSelectOptionsToRecord(field.options)
        const visibleOptions = field.options.filter((option) => option.visible !== false)
        if (visibleOptions.length && !visibleOptions.some((option) => option.key === group.inputs[key].value)) {
          group.inputs[key].value = visibleOptions[0].key
        }
      }
    })
  })

  normalizedConfig.fields
    .filter((field) => field.custom)
    .forEach((field) => {
      const group = groups.find((item) => item.section === field.section)
      if (!group) return
      group.inputs[field.key] = {
        ...(convertRegisterFormFieldToInput(field) as any),
        custom: true,
      }
    })

  return groups
}

export const sanitizeRegisterFormFieldKey = (title: string) => {
  const key = title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

  return `custom_${key || Date.now()}`
}
